import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AppsPage — P1-05 round 2 (registered apps + per-app schema apply).
 *
 * Two-pane operator surface for the `apps` table:
 *
 *   ┌─────────────┬───────────────────────────────────────────┐
 *   │ App picker  │  Section 1: App config (read / edit)      │
 *   │ (col-md-4)  │  Section 2: Apply schema (POST)           │
 *   └─────────────┴───────────────────────────────────────────┘
 *
 * Talks exclusively to:
 *   GET    /v1/admin/apps                         → app list (picker)
 *   GET    /v1/admin/apps/:appId                  → selected app record
 *   PATCH  /v1/admin/apps/:appId                  → mutate the 4 patchable
 *                                                    fields (displayName,
 *                                                    status, allowedAuthMethods,
 *                                                    requireApproval)
 *   DELETE /v1/admin/apps/:appId                  → soft-delete (sets
 *                                                    status='disabled')
 *   POST   /v1/admin/apps/:appId/schema/apply     → run pending .sql files
 *                                                    against the per-app DB
 *
 * Notes on scope:
 *   - Metadata is read-only here (collapsed <details>). The broker block has
 *     its own dedicated page (BrokerCredentialsPage); a full metadata
 *     editor is out of scope for round 2.
 *   - The DELETE route only flips status to 'disabled'. To set 'archived'
 *     we go through PATCH status='archived' (same backing UPDATE column).
 *     The "Disable app" button uses DELETE because that is the documented
 *     soft-delete contract; archive is available via the status dropdown
 *     in the edit form.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
import { consoleHref, useConsoleNav } from '../layout/consoleNav.js';
import { pendingCount } from './joinRequests.js';
import { ClientLogsCard } from './ClientLogsCard.js';
import { AppAiCard } from './AppAiCard.js';
/** Badge for an app in maintenance. Exported for the tests. */
export function MaintenanceBadge({ info }) {
    return (_jsx("span", { className: "badge text-bg-warning", title: info.reason, "data-app-maintenance": info.owner, children: info.owner.startsWith('move:') ? 'moving database' : 'maintenance' }));
}
const EMPTY_DRAFT = {
    displayName: '',
    status: 'active',
    allowedAuthMethodsText: '',
    requireApproval: false,
};
function appToDraft(a) {
    return {
        displayName: a.displayName,
        status: a.status,
        allowedAuthMethodsText: (a.allowedAuthMethods ?? []).join('\n'),
        requireApproval: a.requireApproval,
    };
}
function statusBadgeColor(status) {
    if (status === 'active')
        return 'success';
    if (status === 'disabled')
        return 'secondary';
    if (status === 'archived')
        return 'dark';
    return 'secondary';
}
function StatusBadge({ status }) {
    return _jsx("span", { className: `badge text-bg-${statusBadgeColor(status)}`, children: status });
}
function appliedFileEntry(entry) {
    if (typeof entry === 'string')
        return { filename: entry, appliedAt: null };
    return { filename: entry.filename, appliedAt: entry.appliedAt ?? null };
}
export function AppsPage() {
    const fetcher = useNexusFetch();
    // ---- App list -----------------------------------------------------
    const [apps, setApps] = useState(null);
    const [appsError, setAppsError] = useState(null);
    const [selectedAppId, setSelectedAppId] = useState(null);
    // Maintenance form (core/app_maintenance). null = the reason form is closed; a string = open, with that text.
    const [maintReason, setMaintReason] = useState(null);
    const [maintError, setMaintError] = useState(null);
    const loadApps = useCallback(async () => {
        setAppsError(null);
        try {
            const res = await fetcher('/v1/admin/apps');
            const list = res?.apps ?? [];
            setApps(list);
            setSelectedAppId((prev) => {
                if (prev && list.some((a) => a.appId === prev))
                    return prev;
                // ?app=<id> opens that app (links from AI › Gateway's Apps panel, NEXUS-1393).
                const wanted = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('app') : null;
                if (wanted && list.some((a) => a.appId === wanted))
                    return wanted;
                return list[0]?.appId ?? null;
            });
        }
        catch (e) {
            setApps([]);
            setAppsError(e instanceof Error ? e.message : 'failed to load apps');
        }
    }, [fetcher]);
    useEffect(() => {
        void loadApps();
    }, [loadApps]);
    // ---- Selected app detail -----------------------------------------
    const [app, setApp] = useState(null);
    const [appLoading, setAppLoading] = useState(false);
    const [appError, setAppError] = useState(null);
    const [mode, setMode] = useState('read');
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [submitting, setSubmitting] = useState(false);
    const [formBanner, setFormBanner] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const loadApp = useCallback(async (appId) => {
        setAppLoading(true);
        setAppError(null);
        setMode('read');
        setFormBanner(null);
        setFieldErrors({});
        try {
            const res = await fetcher(`/v1/admin/apps/${encodeURIComponent(appId)}`);
            setApp(res ?? null);
        }
        catch (e) {
            setApp(null);
            setAppError(e instanceof Error ? e.message : 'failed to load app');
        }
        finally {
            setAppLoading(false);
        }
    }, [fetcher]);
    useEffect(() => {
        // A half-typed maintenance reason or an error belongs to the app it was for.
        setMaintReason(null);
        setMaintError(null);
        if (!selectedAppId) {
            setApp(null);
            return;
        }
        void loadApp(selectedAppId);
    }, [selectedAppId, loadApp]);
    // ---- Edit-mode handlers ------------------------------------------
    const startEdit = useCallback(() => {
        setDraft(app ? appToDraft(app) : EMPTY_DRAFT);
        setFormBanner(null);
        setFieldErrors({});
        setMode('edit');
    }, [app]);
    const cancelEdit = useCallback(() => {
        setFormBanner(null);
        setFieldErrors({});
        setMode('read');
    }, []);
    const updateField = useCallback((key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setFieldErrors((fe) => {
            if (!fe[key])
                return fe;
            const next = { ...fe };
            delete next[key];
            return next;
        });
    }, []);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!selectedAppId)
            return;
        setSubmitting(true);
        setFormBanner(null);
        setFieldErrors({});
        // Client-side: require non-empty displayName because the server
        // PATCH ignores blank/whitespace-only displayName silently (it falls
        // through the `body.displayName.trim()` guard). Surface it inline
        // so the operator doesn't get a no-op save.
        const localErrors = {};
        if (!draft.displayName.trim()) {
            localErrors.displayName = ['displayName cannot be empty'];
        }
        if (Object.keys(localErrors).length > 0) {
            setFieldErrors(localErrors);
            setSubmitting(false);
            return;
        }
        const methods = draft.allowedAuthMethodsText
            .split('\n')
            .map((m) => m.trim())
            .filter((m) => m.length > 0);
        const payload = {
            displayName: draft.displayName.trim(),
            status: draft.status,
            allowedAuthMethods: methods,
            requireApproval: draft.requireApproval,
        };
        try {
            await fetcher(`/v1/admin/apps/${encodeURIComponent(selectedAppId)}`, {
                method: 'PATCH',
                json: payload,
            });
            await loadApp(selectedAppId);
            await loadApps(); // status / displayName may have changed
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                setFormBanner(`${err.code}: ${err.message}`);
            }
            else {
                setFormBanner(err instanceof Error ? err.message : 'request failed (unknown error)');
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [draft, fetcher, loadApp, loadApps, selectedAppId]);
    const handleDisable = useCallback(async () => {
        if (!selectedAppId)
            return;
        if (!window.confirm(`Disable app "${selectedAppId}"?\n\n` +
            `This sets status='disabled' (soft delete). The app's keys and ` +
            `data are preserved; you can re-enable it by editing status back ` +
            `to 'active'.`)) {
            return;
        }
        setSubmitting(true);
        setFormBanner(null);
        try {
            await fetcher(`/v1/admin/apps/${encodeURIComponent(selectedAppId)}`, {
                method: 'DELETE',
            });
            await loadApp(selectedAppId);
            await loadApps();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                setFormBanner(`${err.code}: ${err.message}`);
            }
            else {
                setFormBanner(err instanceof Error ? err.message : 'disable failed');
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [fetcher, loadApp, loadApps, selectedAppId]);
    // ---- Maintenance (core/app_maintenance) ----------------------------
    const startMaintenance = useCallback(async (e) => {
        e.preventDefault();
        if (!selectedAppId || maintReason === null)
            return;
        setSubmitting(true);
        setMaintError(null);
        try {
            await fetcher(`/v1/admin/maintenance/apps/${encodeURIComponent(selectedAppId)}`, {
                method: 'POST',
                json: { reason: maintReason.trim() },
            });
            setMaintReason(null);
            await loadApp(selectedAppId);
            await loadApps();
        }
        catch (err) {
            setMaintError(err instanceof NexusAdminApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'could not start maintenance');
            // The request may have taken effect even though the answer was lost: show what is true now.
            void loadApp(selectedAppId);
            void loadApps();
        }
        finally {
            setSubmitting(false);
        }
    }, [fetcher, loadApp, loadApps, maintReason, selectedAppId]);
    const endMaintenance = useCallback(async () => {
        if (!selectedAppId || !app?.maintenance)
            return;
        const ownedByMove = app.maintenance.owner.startsWith('move:');
        if (ownedByMove &&
            !window.confirm(`"${selectedAppId}" is paused by a database move.\n\n` +
                `Ending maintenance now lets requests in while the move may still be switching. ` +
                `Do this only if the move is stuck. End it anyway?`)) {
            return;
        }
        setSubmitting(true);
        setMaintError(null);
        try {
            await fetcher(`/v1/admin/maintenance/apps/${encodeURIComponent(selectedAppId)}`, {
                method: 'DELETE',
                json: ownedByMove ? { override: true } : {},
            });
            await loadApp(selectedAppId);
            await loadApps();
        }
        catch (err) {
            setMaintError(err instanceof NexusAdminApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : 'could not end maintenance');
            void loadApp(selectedAppId);
            void loadApps();
        }
        finally {
            setSubmitting(false);
        }
    }, [app, fetcher, loadApp, loadApps, selectedAppId]);
    // ---- Derived ------------------------------------------------------
    const selectedAppDisplay = useMemo(() => {
        if (!selectedAppId || !apps)
            return null;
        return apps.find((a) => a.appId === selectedAppId) ?? null;
    }, [apps, selectedAppId]);
    // ---- Render -------------------------------------------------------
    return (_jsxs("div", { children: [_jsx("h1", { className: "h3 mb-3", children: "Apps" }), _jsx("p", { className: "text-muted small mb-4", children: "Registered apps on this Nexus instance. Edit the config fields on the right, or apply a staged per-app schema directory against the app's per-app database. Broker credentials have their own page." }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-md-4", children: _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsx("span", { className: "fw-semibold", children: "Apps" }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => void loadApps(), disabled: apps === null, children: "Refresh" })] }), apps === null && (_jsxs("div", { className: "card-body text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading apps\u2026"] })), apps !== null && appsError !== null && (_jsxs("div", { className: "card-body", children: [_jsx("div", { className: "alert alert-danger small mb-2", children: appsError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadApps(), children: "Retry" })] })), apps !== null && appsError === null && apps.length === 0 && (_jsx("div", { className: "card-body text-muted small", children: "No apps registered." })), apps !== null && appsError === null && apps.length > 0 && (_jsx("ul", { className: "list-group list-group-flush", children: apps.map((a) => {
                                        const active = a.appId === selectedAppId;
                                        return (_jsx("li", { className: `list-group-item list-group-item-action ${active ? 'active' : ''}`, role: "button", onClick: () => setSelectedAppId(a.appId), style: { cursor: 'pointer' }, children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsx("div", { className: "fw-semibold", children: a.displayName }), _jsx("code", { className: active ? 'text-light small' : 'text-muted small', children: a.appId })] }), _jsxs("span", { className: "d-flex gap-1", children: [a.maintenance && _jsx(MaintenanceBadge, { info: a.maintenance }), a.status !== 'active' && _jsx(StatusBadge, { status: a.status })] })] }) }, a.appId));
                                    }) }))] }) }), _jsxs("div", { className: "col-md-8", children: [!selectedAppId && (_jsx("div", { className: "card", children: _jsx("div", { className: "card-body text-muted", children: "Select an app on the left to view or edit its config." }) })), selectedAppId && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "card mb-3", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsx("span", { className: "fw-semibold", children: selectedAppDisplay?.displayName ?? selectedAppId }), ' ', _jsx("code", { className: "text-muted small", children: selectedAppId })] }), mode === 'read' && !appLoading && app && (_jsxs("div", { className: "d-flex gap-2", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: startEdit, children: "Edit" }), app.maintenance ? (_jsx("button", { type: "button", className: "btn btn-sm btn-warning", onClick: () => void endMaintenance(), disabled: submitting, children: "End maintenance" })) : (app.status === 'active' && (_jsx("button", { type: "button", className: "btn btn-sm btn-outline-warning", onClick: () => {
                                                                    setMaintError(null);
                                                                    setMaintReason((r) => (r === null ? '' : null));
                                                                }, disabled: submitting, children: "Start maintenance" }))), app.status !== 'disabled' && (_jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void handleDisable(), disabled: submitting, children: "Disable app" }))] }))] }), _jsxs("div", { className: "card-body", children: [appLoading && (_jsxs("div", { className: "text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading app\u2026"] })), !appLoading && appError !== null && (_jsxs(_Fragment, { children: [_jsx("div", { className: "alert alert-danger", children: appError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadApp(selectedAppId), children: "Retry" })] })), maintError && _jsx("div", { className: "alert alert-danger small", children: maintError }), maintReason !== null && app && !app.maintenance && (_jsxs("form", { className: "alert alert-warning", onSubmit: startMaintenance, children: [_jsxs("label", { className: "form-label small fw-semibold mb-1", htmlFor: "app-maintenance-reason", children: ["Put ", app.appId, " in maintenance"] }), _jsxs("div", { className: "d-flex gap-2", children: [_jsx("input", { id: "app-maintenance-reason", className: "form-control form-control-sm", placeholder: "Reason (callers see it)", minLength: 3, maxLength: 300, value: maintReason, onChange: (e) => {
                                                                            const v = e.target.value;
                                                                            setMaintReason(v);
                                                                        }, required: true }), _jsx("button", { type: "submit", className: "btn btn-sm btn-warning", disabled: submitting || maintReason.trim().length < 3, children: "Start" }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => setMaintReason(null), children: "Cancel" })] }), _jsx("div", { className: "form-text", children: "This app's requests will answer 503 \"in maintenance\" and its jobs will not start until you end it. Other apps are not affected." })] })), !appLoading && appError === null && app && mode === 'read' && (_jsx(ReadView, { app: app })), !appLoading && appError === null && app && mode === 'edit' && (_jsx(EditForm, { draft: draft, fieldErrors: fieldErrors, formBanner: formBanner, submitting: submitting, onChange: updateField, onCancel: cancelEdit, onSubmit: handleSubmit })), !appLoading &&
                                                        appError === null &&
                                                        mode === 'read' &&
                                                        formBanner && (_jsx("div", { className: "alert alert-danger mt-3", children: formBanner }))] })] }), _jsx(AppAiCard, { appId: selectedAppId }), _jsx(ApplySchemaCard, { appId: selectedAppId }), _jsx(ClientLogsCard, { appId: selectedAppId })] }))] })] })] }));
}
// ---------------------------------------------------------------------
// Read view
function ReadView({ app }) {
    return (_jsxs("dl", { className: "row mb-0", children: [_jsx(Term, { label: "App ID", children: _jsx("code", { children: app.appId }) }), _jsx(Term, { label: "Display name", children: app.displayName }), _jsx(Term, { label: "Status", children: _jsx(StatusBadge, { status: app.status }) }), _jsx(Term, { label: "Maintenance", children: app.maintenance ? (_jsxs(_Fragment, { children: [_jsx(MaintenanceBadge, { info: app.maintenance }), ' ', _jsx("span", { children: app.maintenance.reason }), _jsxs("div", { className: "text-muted small", children: ["Since ", new Date(app.maintenance.since).toLocaleString(), ". Requests answer 503 with \"retry in", ' ', app.maintenance.retryAfterSeconds, "s\"; jobs do not start. Other apps are not affected."] })] })) : (_jsx("span", { className: "text-muted", children: "off" })) }), _jsx(Term, { label: "Allowed auth methods", children: app.allowedAuthMethods && app.allowedAuthMethods.length > 0 ? (_jsx("ul", { className: "list-unstyled mb-0", children: app.allowedAuthMethods.map((m) => (_jsx("li", { children: _jsx("code", { children: m }) }, m))) })) : (_jsx("span", { className: "text-muted small", children: "\u2014 none \u2014" })) }), _jsx(Term, { label: "Require approval", children: app.requireApproval ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "badge text-bg-warning", children: "yes" }), _jsx(JoinRequestsLink, { appId: app.appId })] })) : (_jsx("span", { className: "badge text-bg-secondary", children: "no" })) }), _jsx(Term, { label: "Created", children: _jsx("code", { className: "small", children: app.createdAt }) }), _jsx(Term, { label: "Updated", children: _jsx("code", { className: "small", children: app.updatedAt }) }), _jsx(Term, { label: "Metadata", children: _jsx(MetadataReadOnly, { metadata: app.metadata }) })] }));
}
function MetadataReadOnly({ metadata, }) {
    if (!metadata || Object.keys(metadata).length === 0) {
        return _jsx("span", { className: "text-muted small", children: "\u2014 empty \u2014" });
    }
    const pretty = JSON.stringify(metadata, null, 2);
    const keys = Object.keys(metadata);
    return (_jsxs("details", { children: [_jsxs("summary", { className: "small", children: [keys.length, " key", keys.length === 1 ? '' : 's', " (", keys.join(', '), ")"] }), _jsxs("div", { className: "form-text small mt-1 mb-2", children: ["Metadata is read-only here. Use ", _jsx("code", { children: "Broker credentials" }), " to edit the ", _jsx("code", { children: "broker" }), " block."] }), _jsx("pre", { className: "small mb-0 p-2 bg-light border rounded", style: { maxHeight: '20rem', overflow: 'auto' }, children: pretty })] }));
}
/**
 * "N pending requests" next to Require approval = yes — the way from an app's
 * record to the place where its self-signups are decided (Apps & work › Join
 * requests, filtered to this app). Counts the app's own request list, the same
 * call that page makes. Navigates in-app through the layout's ConsoleNav; a
 * plain link when mounted without the layout (component tests).
 */
export function JoinRequestsLink({ appId }) {
    const fetch = useNexusFetch();
    const nav = useConsoleNav();
    const [count, setCount] = useState(null);
    useEffect(() => {
        let alive = true;
        setCount(null);
        fetch(`/v1/app/${encodeURIComponent(appId)}/membership/requests`)
            .then((r) => { if (alive)
            setCount(pendingCount(r?.requests ?? [])); })
            .catch(() => { if (alive)
            setCount(null); });
        return () => { alive = false; };
    }, [fetch, appId]);
    const href = consoleHref(nav, 'join-requests', { app: appId });
    const label = count === null ? 'Join requests' : count === 0 ? 'No pending requests' : `${count} pending request${count === 1 ? '' : 's'}`;
    return (_jsx("a", { href: href, className: `ms-2 small ${count ? 'fw-semibold' : 'text-muted'}`, "data-testid": "join-requests-link", "data-count": count ?? '', onClick: (e) => { if (nav) {
            e.preventDefault();
            nav.navigate(href);
        } }, children: label }));
}
function Term({ label, children }) {
    return (_jsxs(_Fragment, { children: [_jsx("dt", { className: "col-sm-4 fw-normal text-muted", children: label }), _jsx("dd", { className: "col-sm-8 mb-2", children: children })] }));
}
function EditForm({ draft, fieldErrors, formBanner, submitting, onChange, onCancel, onSubmit, }) {
    return (_jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [formBanner && _jsx("div", { className: "alert alert-danger", children: formBanner }), _jsx(Field, { label: "Display name", required: true, errors: fieldErrors.displayName, hint: "Human-readable name shown in admin surfaces.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.displayName), value: draft.displayName, onChange: (e) => onChange('displayName', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Status", required: true, errors: fieldErrors.status, hint: "'active' = live; 'disabled' = soft-deleted; 'archived' = cold.", children: _jsxs("select", { className: fieldClass('form-select', fieldErrors.status), value: draft.status, onChange: (e) => onChange('status', e.target.value), children: [_jsx("option", { value: "active", children: "active" }), _jsx("option", { value: "disabled", children: "disabled" }), _jsx("option", { value: "archived", children: "archived" })] }) }), _jsx(Field, { label: "Allowed auth methods", errors: fieldErrors.allowedAuthMethodsText, hint: "One per line. Empty list = all methods enabled by the instance default.", children: _jsx("textarea", { className: fieldClass('form-control', fieldErrors.allowedAuthMethodsText), value: draft.allowedAuthMethodsText, onChange: (e) => onChange('allowedAuthMethodsText', e.target.value), rows: 4, spellCheck: false, style: { fontFamily: 'monospace' } }) }), _jsx(Field, { label: "Require approval", errors: fieldErrors.requireApproval, hint: "If true, new user sign-ups for this app must be approved.", children: _jsxs("div", { className: "form-check", children: [_jsx("input", { id: "apps-require-approval", type: "checkbox", className: "form-check-input", checked: draft.requireApproval, onChange: (e) => onChange('requireApproval', e.target.checked) }), _jsx("label", { className: "form-check-label small", htmlFor: "apps-require-approval", children: "Require operator approval for new users" })] }) }), _jsxs("div", { className: "d-flex gap-2 mt-3", children: [_jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Saving\u2026"] })) : ('Save') }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" })] })] }));
}
function fieldClass(base, errs) {
    return errs && errs.length > 0 ? `${base} is-invalid` : base;
}
function Field({ label, required, errors, hint, children }) {
    return (_jsxs("div", { className: "mb-3", children: [_jsxs("label", { className: "form-label mb-1", children: [label, required && _jsx("span", { className: "text-danger ms-1", children: "*" })] }), children, hint && !errors?.length && _jsx("div", { className: "form-text small", children: hint }), errors?.map((m, i) => (_jsx("div", { className: "invalid-feedback d-block small", children: m }, i)))] }));
}
function ApplySchemaCard({ appId }) {
    const fetcher = useNexusFetch();
    const [schemaDir, setSchemaDir] = useState('');
    const [dryRun, setDryRun] = useState(true);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [errorBanner, setErrorBanner] = useState(null);
    // Reset the result + error when the operator switches apps so we don't
    // show stale output keyed to a different appId.
    useEffect(() => {
        setSchemaDir('');
        setDryRun(true);
        setResult(null);
        setErrorBanner(null);
        setRunning(false);
    }, [appId]);
    const handleApply = useCallback(async (e) => {
        e.preventDefault();
        const dir = schemaDir.trim();
        if (!dir || running)
            return;
        setRunning(true);
        setResult(null);
        setErrorBanner(null);
        try {
            const res = await fetcher(`/v1/admin/apps/${encodeURIComponent(appId)}/schema/apply`, {
                method: 'POST',
                json: { schemaDir: dir, dryRun },
            });
            setResult(res ?? null);
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                setErrorBanner({ code: err.code, message: err.message });
            }
            else {
                setErrorBanner({
                    code: 'UNKNOWN',
                    message: err instanceof Error ? err.message : 'apply failed',
                });
            }
        }
        finally {
            setRunning(false);
        }
    }, [appId, dryRun, fetcher, running, schemaDir]);
    return (_jsxs("div", { className: "card", children: [_jsx("div", { className: "card-header", children: _jsx("span", { className: "fw-semibold", children: "Apply schema" }) }), _jsxs("div", { className: "card-body", children: [_jsxs("form", { onSubmit: handleApply, noValidate: true, children: [_jsxs("div", { className: "mb-3", children: [_jsxs("label", { className: "form-label mb-1", htmlFor: "apps-schemadir", children: ["Schema directory", _jsx("span", { className: "text-danger ms-1", children: "*" })] }), _jsx("input", { id: "apps-schemadir", type: "text", className: "form-control", placeholder: "absolute path on the gateway host, e.g. /home/ekiss71/aquastar/schema", value: schemaDir, onChange: (e) => setSchemaDir(e.target.value), autoComplete: "off", spellCheck: false, disabled: running }), _jsxs("div", { className: "form-text small", children: ["Path is interpreted on the gateway host, not the browser. The operator stages files there (e.g. via ", _jsx("code", { children: "scp" }), ") before running apply."] })] }), _jsxs("div", { className: "mb-3 form-check", children: [_jsx("input", { id: "apps-dryrun", type: "checkbox", className: "form-check-input", checked: dryRun, onChange: (e) => setDryRun(e.target.checked), disabled: running }), _jsx("label", { className: "form-check-label small", htmlFor: "apps-dryrun", children: "Dry run (no DB writes \u2014 just report what would happen)" })] }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: running || !schemaDir.trim(), children: running ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Applying\u2026"] })) : ('Apply schema') })] }), errorBanner && (_jsxs("div", { className: "alert alert-danger mt-3 mb-0", children: [_jsx("div", { className: "fw-semibold", children: errorBanner.code }), _jsx("div", { className: "small", children: errorBanner.message })] })), result && _jsx(SchemaApplyResult, { result: result })] })] }));
}
function SchemaApplyResult({ result }) {
    const files = (result.appliedFiles ?? []).map(appliedFileEntry);
    return (_jsxs("div", { className: "mt-3 border rounded p-3 bg-light", children: [_jsxs("div", { className: "d-flex flex-wrap gap-2 align-items-center mb-2", children: [result.dryRun ? (_jsx("span", { className: "badge text-bg-warning", children: "Dry run" })) : (_jsx("span", { className: "badge text-bg-success", children: "Live" })), _jsx("code", { className: "small", children: result.perAppDb }), result.perAppDbCreated && (_jsx("span", { className: "badge text-bg-info", children: "per-app DB created" }))] }), _jsxs("dl", { className: "row mb-2", children: [_jsx("dt", { className: "col-sm-5 fw-normal text-muted", children: "Files considered" }), _jsx("dd", { className: "col-sm-7 mb-1", children: _jsx("code", { children: result.filesConsidered }) }), _jsx("dt", { className: "col-sm-5 fw-normal text-muted", children: "Newly applied" }), _jsxs("dd", { className: "col-sm-7 mb-1", children: [_jsx("span", { className: "text-success me-1", "aria-hidden": "true", children: "\u2713" }), _jsx("code", { children: result.newlyApplied })] }), _jsx("dt", { className: "col-sm-5 fw-normal text-muted", children: "Skipped" }), _jsxs("dd", { className: "col-sm-7 mb-1", children: [_jsx("span", { className: "text-muted me-1", "aria-hidden": "true", children: "\u2713" }), _jsx("code", { children: result.skipped })] })] }), files.length > 0 ? (_jsxs("table", { className: "table table-sm mb-0", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: "Filename" }), _jsx("th", { scope: "col", children: "Applied at" })] }) }), _jsx("tbody", { children: files.map((f) => (_jsxs("tr", { children: [_jsx("td", { children: _jsx("code", { className: "small", children: f.filename }) }), _jsx("td", { className: "small", children: f.appliedAt ? (_jsx("code", { className: "small", children: f.appliedAt })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) })] }, f.filename))) })] })) : (_jsx("div", { className: "text-muted small mb-0", children: "No new files applied in this run." }))] }));
}
//# sourceMappingURL=AppsPage.js.map